import { skipToken, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { userEnvSettingsKeys, type UserEnvSettings } from './entity';

const STALE_TIME = 60_000;

export interface UserEnvSettingsScope {
  accountId: number;
  userId: number;
  workspaceId: number;
}

/**
 * The account segment must be the account the user's settings live under.
 * While masquerading that is the CIO admin account (id 1), not the account
 * being viewed — `auth.user.account_id` points at the *viewed* account, so
 * callers resolve the segment themselves (mirrors Ember's `replaceAccountID`
 * handling in `user.getUserSettings`).
 *
 * @public
 */
export async function getUserEnvSettings(
  { accountId, userId, workspaceId }: UserEnvSettingsScope,
  signal?: AbortSignal,
): Promise<UserEnvSettings> {
  return hydraUiApi.users.getSettings({
    params: {
      path: { account_id: String(accountId), id: String(userId), env_id: String(workspaceId) },
    },
    signal,
  });
}

interface UseUserEnvSettingsOptions {
  accountId: number | null | undefined;
  userId: number | null | undefined;
  workspaceId: number;
  /** Sample-account access never loads these settings — pass `false` to skip the read. */
  enabled?: boolean;
}

/**
 * Non-suspense read of the user's per-environment settings. Routes to
 * `skipToken` until the authenticated user is known (or while disabled), so
 * callers fall back to their defaults instead of blocking.
 *
 * @public
 */
export function useUserEnvSettingsOptional({
  accountId,
  userId,
  workspaceId,
  enabled = true,
}: UseUserEnvSettingsOptions) {
  const scope = enabled && accountId != null && userId != null ? { accountId, userId, workspaceId } : null;
  return useQuery({
    queryKey:
      scope != null
        ? userEnvSettingsKeys.detail(scope.accountId, scope.userId, scope.workspaceId)
        : ['user-env-settings', 'inactive'],
    queryFn: scope != null ? ({ signal }) => getUserEnvSettings(scope, signal) : skipToken,
    staleTime: STALE_TIME,
  });
}
